import React, { useMemo, useState, useCallback, useEffect, useContext } from 'react';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { useMobileMedia } from '@xo-union/tk-ui-breakpoints';
import kebabCase from 'lodash/kebabCase';
import debounce from 'lodash/debounce';
import TrackingContext from '../../helpers/TrackingContext';
import { PillDropdownOptions, PillWithCheckboxDropdown } from '../ButtonPillDropdown';
import { DismissablePillButton, MorePillButton } from '../ButtonPillDropdown/PillButton';
import css from '../../themePicker.scss';
import { MobileDropdown, FilterValue } from '../ButtonPillDropdown/Mobile';
import { ThemeFilters as ThemeFiltersType, titleCase } from '../../helpers/ThemeFiltersContext';

export const COLORS_ALL_OPTION = 'All Colors';
export const STYLES_ALL_OPTION = 'All Styles';
export const SEASONS_ALL_OPTION = 'All Seasons';

const DO_NOT_LIST_ALL_FILTERS = [COLORS_ALL_OPTION, STYLES_ALL_OPTION, SEASONS_ALL_OPTION];

export type SetFilterCriteria = (colors: string[] | null, styles: string[] | null, seasons: string[] | null) => void;

export type TrackThemeFilters = ({ action, selection }: { action: string; selection?: string | string[] }) => void;

interface Props {
  colors?: string[];
  styles?: string[];
  seasons?: string[];
  presetFilterState?: ThemeFiltersType;
  setFilterCriteria: SetFilterCriteria;
  themeFilterOptionsDisplayNamesMap?: Record<string, string>;
}

type FilterList = string[] | null | undefined;

const generateList = (filtersList: FilterList, displayNameMap: Record<string, string>) =>
  filtersList?.reduce(
    (list: PillDropdownOptions, filter) => [
      ...list,
      { text: displayNameMap[filter] || titleCase(filter), value: filter },
    ],
    []
  ) || [];

const updateFilter = (
  setStateFunction: React.Dispatch<React.SetStateAction<string[] | null>> | ((value: string[] | null) => void)
) => {
  return (newValues: string[]) => {
    if (DO_NOT_LIST_ALL_FILTERS.some(value => newValues.includes(value))) {
      setStateFunction(null);
    } else {
      setStateFunction(newValues);
    }
  };
};

const filterListForValue = (list: string[] | null, value: string) => {
  const filtered = list?.filter(filter => filter !== value);
  return filtered?.length ? filtered : null;
};

const ThemeFilters: React.FC<Props> = ({
  colors,
  styles,
  seasons,
  presetFilterState,
  setFilterCriteria,
  themeFilterOptionsDisplayNamesMap = {},
}) => {
  const [filterElTop, setFilterElTop] = useState<number | undefined>(undefined);
  const [isMobileRailOpen, setOpenMobileRail] = useState(false);
  const { track } = useContext(TrackingContext);
  const matches = useMobileMedia();
  const isMobile = Boolean(matches?.yes);

  const [colorsFilter, setColorsFilter] = useState<string[] | null>(presetFilterState?.colors || null);
  const colorsFilterSelected = useMemo(() => Boolean(colorsFilter?.length), [colorsFilter]);
  const colorsOptionList: PillDropdownOptions = useMemo(() => generateList(colors, themeFilterOptionsDisplayNamesMap), [
    colors,
    themeFilterOptionsDisplayNamesMap,
  ]);

  const [stylesFilter, setStylesFilter] = useState<string[] | null>(presetFilterState?.styles || null);
  const stylesFilterSelected = useMemo(() => Boolean(stylesFilter?.length), [stylesFilter]);
  const stylesOptionList: PillDropdownOptions = useMemo(() => generateList(styles, themeFilterOptionsDisplayNamesMap), [
    styles,
    themeFilterOptionsDisplayNamesMap,
  ]);
  const [seasonsFilter, setSeasonsFilter] = useState<string[] | null>(presetFilterState?.seasons || null);
  const seasonsFilterSelected = useMemo(() => Boolean(seasonsFilter?.length), [seasonsFilter]);
  const seasonsOptionList: PillDropdownOptions = useMemo(
    () => generateList(seasons, themeFilterOptionsDisplayNamesMap),
    [seasons, themeFilterOptionsDisplayNamesMap]
  );

  const showAppliedFilters = Boolean(colorsFilter?.length || stylesFilter?.length || seasonsFilter?.length);

  const isSticky = useCallback(
    (e: Event) => {
      const filterEl = document.getElementById('filtersContainer');
      const scrollTop = window.scrollY;
      if (filterElTop && scrollTop >= filterElTop - 10) {
        filterEl?.classList.add(css.isFixed);
      } else {
        filterEl?.classList.remove(css.isFixed);
      }
    },
    [filterElTop]
  );

  const debouncedIsSticky = useCallback(debounce(isSticky, 100), [isSticky]);

  useEffect(() => {
    const filterEl = document.getElementById('filtersContainer')?.getBoundingClientRect();
    if (filterEl) setFilterElTop(filterEl.top);
  }, []);

  useEffect(() => {
    if (!filterElTop) return;

    window.addEventListener('scroll', debouncedIsSticky);
    return () => {
      window.removeEventListener('scroll', debouncedIsSticky);
    };
  }, [filterElTop, debouncedIsSticky]);

  const filters = useMemo(
    () =>
      [...(colorsFilter || []), ...(stylesFilter || []), ...(seasonsFilter || [])].reduce(
        (arr, val) => (!!val ? [...arr, val] : [...arr]),
        [] as string[]
      ),
    [colorsFilter, stylesFilter, seasonsFilter]
  );

  const mobileFilters = useMemo(() => {
    const clonedFilters = [...filters];
    return clonedFilters.splice(0, 2);
  }, [filters]);

  const handleSetCriteria = useCallback(
    // cannot pass an empty array, we need to pass null
    (colors, styles, seasons) => {
      setFilterCriteria(
        colors?.length ? colors : null,
        styles?.length ? styles : null,
        seasons?.length ? seasons : null
      );
    },
    [setFilterCriteria]
  );

  useEffect(() => {
    handleSetCriteria(colorsFilter, stylesFilter, seasonsFilter);
  }, [colorsFilter, stylesFilter, seasonsFilter, handleSetCriteria]);

  const trackThemeFilters: TrackThemeFilters = useCallback(
    ({ action, selection }) => {
      track('Theme Filter Interaction', {
        action: action,
        selection: selection,
        page: 'theme grid',
      });
    },
    [track]
  );

  const trackRemoveFilter = useCallback(
    (clearChoice: string) => {
      trackThemeFilters({
        action: 'clear filter',
        selection: clearChoice,
      });
    },
    [trackThemeFilters]
  );

  const removeFilter = useCallback(
    (filterValue: string) => {
      if (colorsFilter?.includes(filterValue))
        setColorsFilter(prevFilters => filterListForValue(prevFilters, filterValue));
      if (stylesFilter?.includes(filterValue))
        setStylesFilter(prevFilters => filterListForValue(prevFilters, filterValue));
      if (seasonsFilter?.includes(filterValue))
        setSeasonsFilter(prevFilters => filterListForValue(prevFilters, filterValue));

      trackRemoveFilter(filterValue);
    },
    [colorsFilter, stylesFilter, seasonsFilter, trackRemoveFilter]
  );

  const clearFilters = useCallback(() => {
    setColorsFilter(null);
    setStylesFilter(null);
    setSeasonsFilter(null);
    setFilterCriteria(null, null, null);
    trackRemoveFilter('clear all');
  }, [setFilterCriteria, trackRemoveFilter]);

  const trackPillClick = useCallback(
    selection => {
      trackThemeFilters({
        action: 'open filter',
        selection,
      });
    },
    [trackThemeFilters]
  );

  const trackSaveClick = useCallback(
    filters => {
      trackThemeFilters({
        action: 'update filter',
        selection: filters,
      });
    },
    [trackThemeFilters]
  );

  const handlePillClick = useCallback(
    (filterName: string) => {
      trackPillClick(filterName);
      if (isMobile && !isMobileRailOpen) setOpenMobileRail(true);
    },
    [trackPillClick, setOpenMobileRail, isMobile, isMobileRailOpen]
  );

  const mobileOptions = useMemo(
    () => ({
      colors: colorsOptionList,
      styles: stylesOptionList,
      seasons: seasonsOptionList,
    }),
    [colorsOptionList, stylesOptionList, seasonsOptionList]
  );

  const mobileDefaultValues = useMemo(
    () => ({
      colors: colorsFilter || undefined,
      styles: stylesFilter || undefined,
      seasons: seasonsFilter || undefined,
    }),
    [colorsFilter, stylesFilter, seasonsFilter]
  );

  const numberOfRemainingFilters = filters.length > 3 ? filters.length - 2 : 0;
  const adjustedFilters = isMobile && numberOfRemainingFilters ? mobileFilters : filters;

  const handleMobileSave = (selectedValues: FilterValue) => {
    setStylesFilter(selectedValues.styles || null);
    setColorsFilter(selectedValues.colors || null);
    setSeasonsFilter(selectedValues.seasons || null);
    trackSaveClick([
      ...(selectedValues.styles || []),
      ...(selectedValues.colors || []),
      ...(selectedValues.seasons || []),
    ]);
    setOpenMobileRail(false);
  };

  return (
    <div data-testid="theme-filters" className={css.filtersContainer}>
      <div id="filtersContainer" className={css.filtersHeader}>
        <div className={css.pillsRow}>
          <PillWithCheckboxDropdown
            text="Style"
            options={stylesOptionList}
            defaultValue={stylesFilter || undefined}
            setValue={updateFilter(setStylesFilter)}
            selected={stylesFilterSelected}
            onPillClick={() => handlePillClick('style')}
            filters={filters}
            onSaveClick={trackSaveClick}
            data-testid="theme-styles-filter-pill"
            isMobile={isMobile}
          />
          <PillWithCheckboxDropdown
            text="Color"
            options={colorsOptionList}
            defaultValue={colorsFilter || undefined}
            setValue={updateFilter(setColorsFilter)}
            selected={colorsFilterSelected}
            onPillClick={() => handlePillClick('color')}
            filters={filters}
            onSaveClick={trackSaveClick}
            data-testid="theme-colors-filter-pill"
            isMobile={isMobile}
          />
          <PillWithCheckboxDropdown
            text="Season"
            options={seasonsOptionList}
            defaultValue={seasonsFilter || undefined}
            setValue={updateFilter(setSeasonsFilter)}
            selected={seasonsFilterSelected}
            onPillClick={() => handlePillClick('season')}
            filters={filters}
            onSaveClick={trackSaveClick}
            data-testid="theme-seasons-filter-pill"
            small={true}
            isMobile={isMobile}
          />
          {showAppliedFilters && (
            <DisplayButton
              className={css.clearFilters}
              onClick={clearFilters}
              data-testid="clear-all-filters-sticky-button"
              size="md"
            >
              Clear all
            </DisplayButton>
          )}
        </div>
      </div>

      {showAppliedFilters && (
        <div className={css.pillsRow}>
          {adjustedFilters?.map(appliedFilter => (
            <div key={`${appliedFilter}-pill`}>
              <DismissablePillButton
                data-testid={kebabCase(`filter-pill-${appliedFilter}`)}
                onClick={() => removeFilter(appliedFilter)}
              >
                {themeFilterOptionsDisplayNamesMap[appliedFilter] || appliedFilter}
              </DismissablePillButton>
            </div>
          ))}
          {isMobile && numberOfRemainingFilters ? (
            <MorePillButton numberRemaining={numberOfRemainingFilters} data-testid="filter-pill-more" />
          ) : null}
          <DisplayButton
            className={css.clearAll}
            onClick={clearFilters}
            data-testid="clear-all-filters-button"
            size="md"
          >
            Clear all
          </DisplayButton>
        </div>
      )}

      {isMobile ? (
        <MobileDropdown
          options={mobileOptions}
          defaultValue={mobileDefaultValues}
          onClose={() => setOpenMobileRail(false)}
          onSave={handleMobileSave}
          isOpen={isMobileRailOpen}
        />
      ) : null}
    </div>
  );
};

export default React.memo(ThemeFilters);
